import { c as _c } from "react/compiler-runtime";
/**
 * Web `RowAnimator` slot — the layout-animated per-row wrapper `<Items>`
 * uses to animate list reorders.
 *
 * When a realtime patch live re-ranks a sorted list (manifest `sort:`
 * block → `PagedData._sort` → the codegen-emitted `resortRows`), the
 * row-id array `<Items>` renders reorders. Each row is wrapped in a
 * `<motion.div layout>` keyed by id, so `motion`'s FLIP layout
 * animation tweens the position change instead of the row jumping.
 *
 * `motion/react` is a web-only library (it pulls `react-dom`), so it is
 * confined to `src/platform/web/` and reaches `<Items>` (core) only via
 * the `PlatformAdapter.rowAnimator` slot — never a direct core import.
 *
 * `prefers-reduced-motion` is honoured via `motion`'s `useReducedMotion`:
 * when the user opts out, the wrapper drops the `layout` prop so there is
 * no animation (the row still reorders, just instantly).
 */
import { motion, useReducedMotion } from 'motion/react';
import { createElement } from 'react';
/**
 * Spring tuned for list-row reordering — near-critically damped for a quick,
 * crisp settle (~130 ms) with no overshoot wobble. This fires both for live
 * single-row re-ranks AND for a wholesale sort/filter swap (where every
 * retained row reorders at once), so it is kept snappy to avoid a long
 * grid-wide slide on a sort change.
 */
const REORDER_TRANSITION = {
  type: 'spring',
  stiffness: 900,
  damping: 56,
  mass: 0.9
};
function RowAnimatorWrapper(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  const reduceMotion = useReducedMotion();
  let t1;
  if ($[0] !== children || $[1] !== reduceMotion) {
    t1 = createElement(motion.div, reduceMotion === true ? {
      style: {
        minWidth: 0
      }
    } : {
      layout: true,
      transition: REORDER_TRANSITION,
      style: {
        minWidth: 0
      }
    }, children);
    $[0] = children;
    $[1] = reduceMotion;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
/**
 * Build the web `RowAnimator` slot. Wired into the web adapter by
 * `createWebPlatformAdapter`.
 */
export function createWebRowAnimatorSlot() {
  return {
    Wrapper: RowAnimatorWrapper
  };
}